<template>
  <div :class="{ 'fullscreen-sticky': store.fullScreenSticky }">
    <div class="environment" :class="[store.themeEnvironment]">
      <LoaderComponent v-if="loading" text="Loading PIM" />

      <div v-else-if="shouldBlockPIMForMaintenance" class="overlay d-flex justify-content-center px-md-4 px-2">
        <div class="d-flex justify-content-center align-items-center">
          <div class="server-down-msg">
            <h2>
              {{ appTranslate('PIM is unavailable due to maintenance. This message will disappear when PIM is available again') }}
            </h2>
            <a class="link" @click="AuthService.signOutWithMessage()">{{ appTranslate('Sign out') }}</a>
          </div>
        </div>
      </div>

      <router-view v-else-if="route.meta.fullscreen" />

      <template v-else>
        <TopNavComponent v-if="store.isSignedIn && (!productsViewStore.fullscreen || route.name !== 'products')" />

        <div class="position-relative" :class="{ 'scrollbar-fix': !route.meta.fullWidth }">
          <div class="dev-container">
            <div v-if="deviatingDevMode?.length" class="mode deviant">
              <!-- :style="`top: ${productsViewStore.fullscreen ? 0 : store.topMenuHeight}`" -->
              <small v-for="mode of deviatingDevMode" :key="mode" class="d-block">{{ mode }}</small>
            </div>
            <div v-if="shouldShowSmallMaintenanceMsg" class="mode maintenance">PIM IS IN MAINTENANCE MODE!</div>
          </div>

          <router-view v-slot="{ Component }">
            <keep-alive :include="keepAliveComponents">
              <ErrorView
                v-if="route.query.error === 'unauthorized'"
                :title="appTranslate('401: Access denied')"
                :text="appTranslate(`You do not have access to &quot;%s&quot;.`, [route.path])"
              />
              <component :is="Component" v-else :key="route.path" />
            </keep-alive>
          </router-view>
        </div>
      </template>

      <div v-if="availableNewVersion && !muteNewVersionMsg" class="notification alert alert-dark d-print-none">
        <button
          v-if="!availableNewVersion.isMajorVersion"
          type="button"
          class="close ml-4"
          style="font-size: 2.5em"
          aria-label="Close"
          @click="muteNewVersionMsg = true"
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <div>{{ appTranslate('A new version is available') }}!</div>
        <small
          v-if="availableNewVersion.isMajorVersion"
          class="d-block mb-3"
          v-html="appTranslate('The new version will be loaded automatically in %s seconds', [`<strong>${displayedReloadTimer}</strong>`])"
        />
        <em v-if="availableNewVersion.msg" class="mb-3"> "{{ availableNewVersion.msg }}" </em>

        <a class="link font-weight-bold" @click="reload()">
          {{ appTranslate(availableNewVersion.isMajorVersion ? 'Update now' : 'Update') }}
        </a>
      </div>

      <div id="modals" />
    </div>
    <MessagesOutput />
  </div>
</template>

<script lang="ts" setup>
import TopNavComponent from '@/components/TopNavComponent.vue'
import LoaderComponent from '@/components/simple/LoaderComponent.vue'
import { AppVersionType } from '@/models/Settings'
import { useDefaultStore } from '@/store'
import { useProductsViewStore } from '@/store/productsViewStore'
import { computed, onMounted, onUpdated, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MessagesOutput from './components/messages/MessagesOutput.vue'
import { useTranslation } from './composables/useTranslation'
import { keepAliveComponents } from './router'
import AuthService from './services/AuthService'
import DataService from './services/DataService'
import ErrorView from './views/ErrorView.vue'

// -----------
// -- Setup --
// -----------
const store = useDefaultStore()
const productsViewStore = useProductsViewStore()
const { appTranslate } = useTranslation()
const route = useRoute()
const router = useRouter()

// -------------------
// -- Reactive vars --
// -------------------
const loading = ref(true)
const displayedReloadTimer = ref(0)
const timerInterval = ref<number>()
const muteNewVersionMsg = ref(false)

// --------------------
// -- Computed props --
// --------------------
const deviatingDevMode = computed(() => {
  const dataMode = {
    project: DataService.domain,
    api: store.apiHost,
  }

  if (store.themeEnvironment === 'development') {
    return [
      dataMode.api.includes('localhost') ? undefined : `API: ${dataMode.api}`,
      dataMode.project?.includes('dev') ? undefined : `Prj: ${dataMode.project}`,
    ].filter((m) => !!m)
  } else {
    return undefined
  }
})

const availableNewVersion = computed(() => {
  const newAdminVersion = getNewVersion('admin')
  const newPublicVersion = getNewVersion('public')

  if (store.currentUser?.role === 'superAdmin' && newAdminVersion && (newAdminVersion.isMajorVersion || !newPublicVersion?.isMajorVersion)) {
    return newAdminVersion
  }

  if (newPublicVersion) {
    return newPublicVersion
  }

  return undefined
})

const shouldQueueForcedReload = computed(() => availableNewVersion.value?.isMajorVersion && !timerInterval.value)
const shouldBlockPIMForMaintenance = computed(
  () => store.settings?.app.isInMaintenanceMode && !store.userPrivileges?.has('accessToDevTools') && route.name !== 'sign-in',
)
const shouldShowSmallMaintenanceMsg = computed(
  () => store.settings?.app.isInMaintenanceMode && (store.userPrivileges?.has('accessToDevTools') || route.name === 'sign-in'),
)

// --------------
// -- Watchers --
// --------------
watch(
  () => store.settings?.app.version,
  () => {
    if (availableNewVersion.value) {
      muteNewVersionMsg.value = false

      if (shouldQueueForcedReload.value) {
        console.log('detected new major version!')
        queueForcedReloadTimer()
      } else {
        console.log('detected new minor version')
      }
    } else {
      window.clearInterval(timerInterval.value)
      timerInterval.value = undefined
    }
  },
)

watch(
  () => store.fullScreenSticky,
  () => {
    updateBodyStyle()
  },
)

watch(
  () => store.isSignedIn,
  (isSignedIn) => {
    if (isSignedIn) {
      router.replace({ ...route, force: true }) // trigger route guard
    } else {
      router.replace({ name: 'sign-in', query: route.query })
    }
    loading.value = false
  },
)

// ---------------------
// -- Lifecycle hooks --
// ---------------------
onMounted(async () => {
  updateBodyStyle()
  await DataService.loadSettings()
  store.loadedAppDate = new Date()

  if (store.isSignedIn) {
    loading.value = false
  }
})

// -------------
// -- Methods --
// -------------
const updateBodyStyle = () => {
  // ensure scrollbar takes up space in fullscreen mode (using css rules excluding the width of the scrollbar)
  if (store.fullScreenSticky) {
    document.body.classList.add('overflow-y-scroll')
    document.body.classList.remove('overflow-y-auto')
  } else {
    document.body.classList.add('overflow-y-auto')
    document.body.classList.remove('overflow-y-scroll')
  }
}

const reload = () => {
  muteNewVersionMsg.value = true
  window.location.reload()
  loading.value = true
}

const getNewVersion = (type: AppVersionType): { isMajorVersion: boolean; msg?: string; type: AppVersionType } | undefined => {
  const newVersionIsAvailable = (type: AppVersionType, keyType: 'minorUpdateKey' | 'majorUpdateKey') => {
    // store.loadedAppVersion && store.loadedAppVersion?.[type]?.[keyType]?.toString() !== store.settings?.app?.version?.[type]?.[keyType]?.toString()
    const latestVersion = store.settings?.app?.version?.[type]?.[keyType]
    return store.loadedAppDate && latestVersion && store.loadedAppDate?.toISOString() < latestVersion.toDate()?.toISOString()
  }

  const majorUpdateExists = newVersionIsAvailable(type, 'majorUpdateKey')
  const minorUpdateExists = newVersionIsAvailable(type, 'minorUpdateKey')

  if (majorUpdateExists || minorUpdateExists) {
    return { isMajorVersion: !!majorUpdateExists, msg: store.settings?.app?.version?.[type]?.msg || '', type }
  }
}

const queueForcedReloadTimer = () => {
  displayedReloadTimer.value = store.settings?.app.forcedReloadTimeSeconds || 60
  timerInterval.value = window.setInterval(() => {
    if (availableNewVersion.value) {
      if (displayedReloadTimer.value > 0) {
        displayedReloadTimer.value--
      } else {
        clearInterval(timerInterval.value)
        reload()
      }
    }
  }, 1000)
}
</script>

<style lang="scss" scoped>
.environment {
  &::before,
  &::after {
    content: '';
    position: fixed;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    z-index: 20;
    pointer-events: none;
    // border: 3px solid transparent;

    @media print {
      display: none;
    }
  }

  &::before {
    border-top: none;

    @media (min-width: 576px) {
      font-size: 12px;
    }
  }

  &::before {
    border-color: var(--theme-bg);
  }

  &.edit-mode::after {
    border-color: orange;
    z-index: 11;
  }
}

#app > .environment::before {
  @media (min-width: 576px) {
    width: 100vh !important;
    transform-origin: top left;
    transform: rotate(-90deg) translateX(-100%);
    top: 0;
    left: 0;
  }
}

#modals {
  z-index: 1050;
}

.fullscreen-sticky {
  width: fit-content;
}

em {
  font-size: 0.8em;
  display: block;
}

.dev-container {
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 0.25em;

  z-index: 99999;
  margin-top: 2px;
  right: 3px;
  pointer-events: none;

  .mode {
    font-size: 14px;
    padding: 5px;
    border-radius: 5px;
  }

  .deviant {
    background-color: rgba(255, 180, 82, 0.7);
  }

  .maintenance {
    background-color: rgb(245 97 97 / 80%);
    font-weight: bold;
  }

  @media print {
    display: none;
  }
}
.overlay {
  background-color: #fffc;
}

.server-down-msg {
  padding: 2em;
  border: 1px solid grey;
}
</style>

<!-- Global styles -->
<style lang="scss">
@use 'sass:color';
@use '@/styles/animations.scss';

@font-face {
  font-family: 'Quicksand';
  src: url('@/assets/fonts/Quicksand-VariableFont_wght.ttf') format('truetype');
  font-weight: normal;
}

@font-face {
  font-family: 'Montserrat';
  src: url('/fonts/Montserrat-VariableFont_wght.ttf') format('truetype');
  font-weight: 300;
}

:root {
  --text-colour: #222;
  --link-colour: #888;
  --bright-grey: #eee;
  --light-grey: #ddd;
  --disabled: #ccc;
  --dimmed: #555a;
  --selected: #0075ff;

  --component-colour: tomato;
  --outer-group-colour: green;
  --inner-group-colour: blue;

  --modified-colour: #9fd5b7;
  --modified-background: #e5f5e6;

  --preliminary-colour: #f00;
  --preliminary-colour-placeholder: #f008;
  --placeholder: #2225;
  --new-colour: #00f;
  // $empty-preliminary-colour: #f008 !important;

  --table-primary-header-colour: #e6e6e6;
  --table-secondary-header-colour: #fafafa;
}

.environment {
  &.production {
    --theme-bg: white;
    // --theme-header-colour: #0056b3;
    --theme-header-colour: #222;
    --theme-link-colour: #007bff;
    // --theme-header-colour: #007bff;
    // --theme-selected-primary: #0056b3;
    --theme-selected-primary: #007bff;
    --theme-selected-secondary: #bfdcfc;
  }

  &.beta {
    // --theme-bg: #a9c9da;
    --theme-bg: #a1c7d9;
    --theme-header-colour: #222;
    --theme-link-colour: black;
    --theme-selected-primary: var(--theme-bg);
    --theme-selected-secondary: #d9eff8;
  }

  &.stage {
    --theme-bg: rgb(200, 88, 88);
    --theme-header-colour: white;
    --theme-link-colour: white;
    --theme-selected-primary: var(--theme-bg);
    --theme-selected-secondary: #ffdcd5;
  }

  &.development {
    --theme-bg: rgb(88, 150, 200);
    --theme-header-colour: white;
    --theme-link-colour: white;
    --theme-selected-primary: var(--theme-bg);
    --theme-selected-secondary: #bfdcfc;
  }
}

html {
  height: 100%;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
  // scrollbar-gutter: stable;
}

.sticky-wrapper {
  position: sticky;
  left: 0;
  margin: 0;
}

.container-padding {
  padding-top: 1.5rem;
  padding-bottom: 0.5rem;
}

.notification {
  position: fixed;
  bottom: 5em;
  right: 10px;
  z-index: 10000;
}

body {
  height: 100%;
  font-family: Quicksand, Calibri, 'Segoe UI', sans-serif;

  color: var(--text-colour);
  text-shadow: 1px 1px 2px #fff9;
  overflow-y: auto;
  overscroll-behavior-x: contain;

  @media screen and (min-width: 576px) and (max-height: 100vh) {
    // prevents content jumping due to showing / hiding scrollbar

    #app .scrollbar-fix {
      margin-left: calc(100vw - 100%); // width of scrollbar
      margin-right: 0;
    }
  }

  .fullscreen-sticky nav {
    position: sticky;
    // width: 100%;
    margin: 0;
    left: 0;
  }

  a[href] {
    cursor: pointer;
  }

  a,
  input,
  select {
    outline-color: var(--light-grey);
  }

  .link {
    color: var(--text-colour);
    outline-color: var(--bright-grey);
    background-color: transparent;
    border: none;
    text-decoration: none;
    cursor: pointer;
  }

  button:disabled,
  .link:disabled,
  .disabled {
    cursor: default;

    &:not(.btn-warning) {
      color: var(--disabled) !important;
    }

    img {
      opacity: 0.2;
    }
  }

  img.disabled {
    opacity: 0.2;
  }

  .disabled-input {
    background-color: #e9ecef;
  }

  .no-outline {
    outline: none;
  }

  .right {
    position: absolute;
    margin: 1em;
    right: 0;

    .messages {
      position: relative;
    }
  }

  #app {
    height: 100%;
  }
}

input[type='search'] {
  &::placeholder {
    font-style: italic;
  }
}

img {
  user-select: none;
}

.clear {
  padding-top: 0;
  padding-bottom: 0;
  font-size: 1.5em;
}

.overlay {
  position: fixed;
  z-index: 1;
  overflow: hidden;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
}

$borderRadius: 3px;
$emphasizedBorderSize: 2px;
.checkbox {
  position: relative;
  padding: 0 0.65em;
  margin-right: 0.3em;
  border: 1px solid grey;
  border-radius: $borderRadius;

  &.checked,
  &.selected-parent {
    border-color: var(--selected);
  }
  &.selected-parent:not(.checked) {
    background-color: #6cb1ff;
  }
  &.checked {
    background-color: var(--selected);
  }

  &.selected-child {
    &::before {
      content: '';
      position: absolute;
      left: calc(-1 * $emphasizedBorderSize / 2);
      width: calc(100% + #{$emphasizedBorderSize});
      height: calc(100% + #{$emphasizedBorderSize});
      border: 2px solid var(--selected);
      border-radius: $borderRadius;
    }
  }

  &.checked::after {
    content: ' ';
    position: absolute;
    top: 2%;
    left: 30%;
    width: 0.5em;
    height: 1em;
    border: solid white;
    border-width: 0 0.25em 0.25em 0;
    transform: rotate(40deg);
  }

  // &.negated {
  //   border: 2px solid red;
  // }
}
.multi-checkbox {
  border: 1px solid grey;
  border-radius: 2px;
  width: 20px;
  height: 20px;
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;

  &.include,
  &.exclude {
    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
    }
  }

  &.include {
    border-color: var(--primary);
    background-color: var(--primary);

    &::before {
      width: 7px;
      height: 14px;
      border: 3px solid white;
      border-top: 0;
      border-left: 0;
      transform: translate(6px, 2px) rotate(35deg);
    }
  }
  &.implicitly-included {
    background-color: #6cb1ff;
  }
  &.exclude {
    background-color: tomato;

    &::before,
    &::after {
      width: 17px;
      height: 17px;
    }

    &::before {
      border-top: 3px solid white;
      transform: translate(-3px, 7px) rotate(45deg);
    }

    &::after {
      border-top: 3px solid white;
      transform: translate(7px, 7px) rotate(315deg);
    }
  }
}

input[type='checkbox'].new-checkbox {
  appearance: none;
  -moz-appearance: none;
  -webkit-appearance: none;
  -o-appearance: none;
  outline: none;

  &::before {
    content: '';
    $size: 20px;
    width: $size;
    height: $size;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 2px;
    border: 1px solid var(--text-colour);
  }

  &:checked::before {
    content: '\f00c';
    font-family: 'FontAwesome';
    border-color: var(--selected);
    background-color: #0075ff;
    color: white;
  }

  &.selected-parent:not(:checked)::before {
    border-color: var(--selected);
    background-color: #6cb1ff;
  }
}

.dropdown-menu,
.minimalistic {
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  &.minimalistic-thin {
    &::-webkit-scrollbar {
      width: 5px;
      height: 5px;
    }
  }
}

.sticky-action-buttons {
  position: sticky;
  bottom: 0;
  padding: 1em;
  background-color: white;
  border-top: 1px solid #ddd;
  z-index: 2;

  @media print {
    display: none;
  }
}

.flags {
  user-select: none;

  img {
    margin-right: 0.5rem;
  }
}

.flag {
  user-select: none;
}

.flags,
.flags-small,
.flags-tiny {
  .faded,
  img.faded {
    opacity: 0.2;
  }
}

.flags-small img {
  margin-right: 0.25rem;
}

.flags-tiny img {
  margin-right: 1px;
}

// fixed height for displaying multiple flags vertically
.flags img,
img.flag,
i.flag {
  --flag-width: 2.3rem;
  width: var(--flag-width);
}
.flags-small img,
img.small-flag,
i.small-flag {
  --flag-width: 1.5rem;
  width: var(--flag-width);
}

.flags-tiny img,
img.tiny-flag,
i.tiny-flag {
  --flag-width: 1rem;
  width: var(--flag-width);
}

.no-flag {
  --flag-width: 1rem; // default
  width: var(--flag-width);
  height: var(--flag-width);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-size: var(
    --flag-width
  ); // inherit from a flag class to make the icon scale dynamically. e.g. use class="no-flag tiny-flag" to inherit the width of tiny-flag

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 65%;
    background-color: white;
    box-shadow: 0 0 1px #000c;
    // border: 0.2px solid #888;
    // border: 0.2px solid #555;
  }
}

// speech bubble clip for country flag
img.language {
  user-select: none;
  width: unset;
  height: var(--flag-width, 1.5rem);
  // clip-path: polygon(2% 21%, 98% 21%, 98% 75%, 51% 76%, 21% 100%, 21% 75%, 2% 75%);
  clip-path: polygon(4% 32%, 6% 30%, 94% 30%, 96% 32%, 96% 67%, 94% 69%, 50% 69%, 21% 88%, 21% 69%, 6% 69%, 4% 67%);
}

.spinner-border {
  border: 3px solid;
  border-color: #00000012 !important;
  border-bottom-color: #00000080 !important;
  animation: spinner-border 0.65s linear infinite;
}

.faded-spinner {
  animation: spinner-border 0.65s linear infinite;
  mask: radial-gradient(farthest-side, rgba(0, 0, 0, 0) calc(100% - 4px), rgb(0, 0, 0) 0px);
  -webkit-mask: radial-gradient(farthest-side, rgba(0, 0, 0, 0) calc(100% - 4px), rgb(0, 0, 0) 0px);
  background:
    radial-gradient(farthest-side, grey, 94%, #0000) top/ 4px 4px no-repeat,
    conic-gradient(#0000 30%, grey);
}

.refresh-spinner {
  border-width: 0.1em !important;
}

.small-errormsg {
  color: red;
  font-size: 14px;
}

.inactive img,
img.inactive {
  opacity: 0.5;
}

.active img img.active,
.inactive:hover img,
image.inactive:hover {
  opacity: 1;
}

@media screen {
  .edit-mode {
    input[type='text'],
    input[type='search'],
    *[contenteditable='true']:not(.multi-line) {
      height: unset;

      &:focus {
        border-color: #80bdff;
        outline-color: #bfdeff;
        // box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      }
    }

    label {
      margin: 0;
    }

    .component {
      border: 2px dashed var(--component-colour) !important;

      box-shadow: 0 0 4px var(--component-colour);
      margin: 2px;
      padding: 0.5em;

      h5 {
        text-align: center;
      }

      > :not(.preview) {
        button.add,
        .group {
          border: 2px dashed var(--outer-group-colour);
          box-shadow: 0 0 3px var(--outer-group-colour);
          padding: 0.5em;

          box-sizing: border-box;

          button.add,
          &.inner-group {
            border-color: var(--inner-group-colour);
            box-shadow: 0 0 2px var(--inner-group-colour);
          }
        }
      }

      .settings {
        user-select: none;
        width: 100%;
        background-color: #eee;
        padding: 0.5em;
        margin-bottom: 0.5em;
      }
    }
  }
}

.tag {
  display: inline-flex;
  align-items: center;
  margin: 5px;
  padding: 2px 7px;
  border-radius: 15px;
  border-width: 0;
  background-color: var(--light-grey);
  user-select: none;
  box-shadow: 1px 1px 2px #bbb;
}

button.tag {
  &:not(:disabled) {
    &::after {
      content: '\00D7';
      line-height: 1em;
      margin-left: 0.2em;
      font-size: 1.6em;
    }

    &:focus {
      outline: 1px solid grey;
    }
  }
}

$activeFilterColour: blue;
.filter-active {
  color: $activeFilterColour !important;
}
input[type='search'],
input[type='date'],
button,
select {
  &.filter-active {
    vertical-align: baseline;
    border: 1px solid $activeFilterColour !important;
    outline: 1px solid $activeFilterColour !important;
  }
}

.required::before {
  content: '* ';
  color: red;
}

.deleting {
  background-color: #99000066 !important;
}

.icon-disabled {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    height: 1.25em;
    left: 6px;
    top: -2px;
    width: 2px;
    background-color: red;
    transform: rotate(-45deg);
  }

  &.disabled::after {
    background-color: var(--disabled) !important;
  }
}

// dev buttons
.dev {
  position: relative;

  &.btn {
    border: 1px solid grey;
  }

  &::after {
    content: 'DEV \2605';
    position: absolute;
    top: 0;
    left: 2px;
    border: unset;
    font-size: 0.5em;
    font-weight: bold;
  }
}

.max {
  max-width: 100vw;
}

.border-none {
  border: none;
}

.v-html {
  p {
    margin-bottom: 0;
  }
}

.text {
  color: var(--text-colour);
  font-style: normal;
}

body.cursor-col-resize * {
  cursor: col-resize !important;
}

.info-circle {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  $size: 14px;
  width: $size;
  height: $size;
  font-size: 0.75em;
  color: white;
  line-height: normal;

  &.inactive {
    opacity: 0.3;
  }
}

.discreet-availability-status {
  $size: 12px;
  width: $size;
  height: $size;
  border-radius: 50%;
  display: inline-block;
}

input[type='number'].no-counter {
  display: inline-block;

  -moz-appearance: textfield;
  appearance: textfield;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

// bootstrap complements
.form-control.focused {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.inline {
  display: inline;
  width: unset;
}

.border-red {
  border-color: red !important;
}

.text-red {
  color: red;
}

.hidden {
  visibility: hidden;
}

.w-fit-content {
  width: fit-content;
}

.w-max-content {
  width: max-content;
}

.underlined {
  text-decoration: underline !important;
}
.line-through {
  text-decoration: line-through !important;
}

.height-unset {
  height: unset;
}

.d-contents {
  display: contents !important;
}

.no-scroll {
  overflow: hidden !important;
}

.no-scroll-y {
  overflow-y: hidden !important;
}

.vertical-align-top {
  vertical-align: top;
}

.w-33 {
  width: 33% !important;
}

.w-66 {
  width: 66% !important;
}

.h-100 {
  height: 100% !important;
}

.overflow-y-scroll {
  overflow-y: scroll;
}

.overflow-y-auto {
  overflow-y: auto;
}

.white-space-nowrap {
  white-space: nowrap;
}

.btn-outline-white {
  $colour: #6c757d;
  background-color: white;
  // border-radius: 3px;
  border: 1px solid #aaa;
  color: $colour;

  &:disabled {
    background-color: #aaa;
  }
  &:hover:not(:disabled) {
    background-color: $colour;
    color: white;
  }
}

.light-grey {
  color: var(--light-grey);
}

.text-disabled {
  color: var(--disabled);
}

.gap-1 {
  gap: 0.25em;
}
.gap-2 {
  gap: 0.5em;
}
.gap-3 {
  gap: 1em;
}
.gap-4 {
  gap: 1.5em;
}
.gap-4 {
  gap: 3em;
}

.pointer-events-none {
  pointer-events: none;
}

// -------

$collapsableMenuTransition: 0.3s ease;
.sticky-wrapper,
.menu-header,
th.index,
th.table-header,
td.horizontally-sticky {
  transition:
    left $collapsableMenuTransition,
    width $collapsableMenuTransition;
}
.archive {
  transition: grid-template-columns $collapsableMenuTransition;
}

.dropdown-menu {
  overflow: auto;
  overscroll-behavior: contain;
  max-height: 75vh;

  &.down {
    // prevent bootstrap flipping the menu upwards avoiding z-index problems with
    transform: translateY(1.5em) !important;
  }
}

// ---------------------------------------------
// -- Rules for "preliminary" & "placeholder" --
// ---------------------------------------------
@mixin placeholder() {
  font-style: italic;
}
@mixin grey-placeholder() {
  @include placeholder;
  color: var(--placeholder);
}

.placeholder:not(.static):not(.typing) input::placeholder, // input placeholder
 .placeholder:not(.static):not(.typing) .no-value.placeholder // custom input placeholder ?? not sure if necessary
{
  @include grey-placeholder();
}

// context menu
.not-applicable-text {
  @include grey-placeholder();
}

.option .not-applicable {
  @include grey-placeholder();
}

// tiny property state component, editable component
.placeholder-text {
  @include grey-placeholder();

  &.preliminary-text {
    color: var(--preliminary-colour-placeholder) !important;
  }
}

.preliminary {
  input::placeholder {
    color: var(--preliminary-colour-placeholder) !important;
  }
}

.preliminary:not(.placeholder) :not(input) .state, // editable component
.preliminary-text, // context menu, tiny property state component
.preliminary a, // filter links
.preliminary.no-value .label, // labels (headers)
.preliminary :not(input).state // custom dropdown
{
  color: var(--preliminary-colour) !important;
}

// filter links
.preliminary a {
  text-decoration: underline;
}

// empty preliminary in table view and product view
table.products .preliminary.no-value {
  outline: 1px solid var(--preliminary-colour);
  outline-offset: -1px;
}

// hide preliminary placeholders in some cases
table.products,
.product-view .edit-mode {
  .preliminary.no-value {
    input::placeholder, // custom dropdown
    .placeholder-text  // editable component
  // .preliminary-text:not(.sku)
    {
      opacity: 0;
    }
  }
}

.product-view .edit-mode .preliminary.no-value .state {
  outline: 1px solid var(--preliminary-colour);
}

// context menu
button:disabled .preliminary-text {
  opacity: 0.5;
}

.no-value .label {
  // color: var(--disabled);

  img {
    // opacity: 0.4 !important;
  }

  color: grey;
  opacity: 0.4;
}

.no-value .state {
  user-select: none;
}

//  Global property related css (also needed for context menu)

// ------------------------
// -- Rules for modified --
// ------------------------
.modified-bg,
.modified:not(.static) .state:not(.bg-white) {
  background-color: var(--modified-background) !important; // necessary in productview
}

.modified-border {
  border: 2px solid var(--modified-colour) !important;
}

.modified-border-thin {
  border: 1px solid var(--modified-colour) !important;
}

.modified-border:focus,
.modified.state {
  outline: 1px solid var(--modified-colour) !important;
}

.modified-glossy-bg {
  $bgColour: #e5f5e6; // modified-background
  background: linear-gradient(
    color.scale($bgColour, $lightness: 20%),
    color.scale($bgColour, $lightness: -3%) 75%,
    color.scale($bgColour, $lightness: -2%)
  ) !important;
}

.modified {
  .label {
    color: var(--modified-colour) !important;
  }

  :not(.discreet) .form-control {
    vertical-align: baseline;
    outline: 1px solid var(--modified-colour);
    border-color: var(--modified-colour) !important;
  }
}
</style>
